import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { axiosInstance } from "@/shared/api/axiosConfig";
import { invalidateDeckMainCardByIdQueries } from "@/cards/common/utils/deckMainCardByIdQuery";
import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import { inviteImpactPartner, readApiErrorMessage, verifyImpactPage } from "../api/impactContribution.api";
import type { ImpactPageVerifyStatus } from "../api/impactContribution.api";
import { isImpactCardCreator } from "../utils/impactCardType";
import { columnExperienceHasContent, columnImageUrls } from "../utils/columnExperience";
import { useAnchorCubes } from "./useAnchorCubes";
import { isPartnerImpactCube } from "../utils/anchorCubes";

import type {
  ImpactCardData,
  ImpactFieldUpdate,
  ImpactLocalPartner,
} from "../utils/impactCardData";
import {
  serializeImpactFieldUpdate,
  serializeImpactLocalPartner,
} from "../utils/impactCardData";
import type { AnchorCube, AnchorCubeColumn } from "../utils/anchorCubes";

const UPDATES_CUBE_ID = "cube-thanks";

type UseImpactLocalPartnerOptions = {
  mainCardId?: number;
  cardTalentId?: number | string;
  cardContent?: unknown;
  data: ImpactCardData;
};

function reportFromCube(cube: AnchorCube): ImpactFieldUpdate {
  const images = columnImageUrls(cube.experience);
  const mediaUrl = cube.experience?.url?.trim();
  const isVideo = cube.experienceType === "Video" || cube.experienceType === "Youtube";
  return {
    id: String(cube.pageId || cube.id),
    title: cube.title || (isVideo ? "Video from the place" : "Photo from the place"),
    description: cube.description || cube.experience?.description || undefined,
    imageUrl: !isVideo ? images[0] || mediaUrl || undefined : undefined,
    imageUrls: !isVideo && images.length ? images : undefined,
    videoUrl: isVideo ? mediaUrl || undefined : undefined,
    locationName: cube.experience?.address || undefined,
    status: "pending",
  };
}

function reportFromColumn(column: AnchorCubeColumn): ImpactFieldUpdate {
  const images = columnImageUrls(column.experience);
  const mediaUrl = column.experience?.url?.trim();
  const isVideo = column.experienceType === "Video" || column.experienceType === "Youtube";
  return {
    id: String(column.pageId || column.id),
    title: column.title || (isVideo ? "Video from the place" : "Photo from the place"),
    description: column.description || column.experience?.description || undefined,
    imageUrl: !isVideo ? images[0] || mediaUrl || undefined : undefined,
    imageUrls: !isVideo && images.length ? images : undefined,
    videoUrl: isVideo ? mediaUrl || undefined : undefined,
    locationName: column.experience?.address || undefined,
    mapsUrl:
      column.experienceType === "Location" ? mediaUrl || column.experience?.address : undefined,
    status:
      column.reviewStatus === "Rejected"
        ? "rejected"
        : column.reviewStatus === "Verified" || column.verified
          ? "approved"
          : "pending",
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export function useImpactLocalPartner({
  mainCardId,
  cardTalentId,
  cardContent,
  data,
}: UseImpactLocalPartnerOptions) {
  const queryClient = useQueryClient();
  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();
  const [saving, setSaving] = useState(false);
  const [invitedPartner, setInvitedPartner] = useState<ImpactLocalPartner | null>(null);

  const viewerId = Number(userInfo?.id) || 0;
  const talentHeader =
    (cardTalentId != null && String(cardTalentId).trim()) ||
    (currentProfile?.id != null && String(currentProfile.id).trim()) ||
    "";
  const isCreator = isImpactCardCreator(cardTalentId, currentProfile?.id);
  const partner = data.localPartner ?? invitedPartner ?? undefined;
  const assigned =
    Boolean(partner?.assigned) && partner?.userId != null && partner.userId > 0;
  const isPartner = assigned && viewerId > 0 && partner?.userId === viewerId;
  const { cubes, addCube, addColumn, editCube, editColumn, removeCube, removeColumn } = useAnchorCubes();
  const updatesCube = cubes.find((cube) => cube.id === UPDATES_CUBE_ID);

  const findUpdateColumn = (id: string) =>
    updatesCube?.columns.find(
      (column) => column.id === id || String(column.pageId || "") === String(id)
    );

  const findPartnerCube = (id: string) =>
    cubes.find(
      (cube) =>
        isPartnerImpactCube(cube) &&
        (cube.id === id || String(cube.pageId || "") === String(id))
    );

  const findPartnerContent = (id: string) => {
    const cube = findPartnerCube(id);
    if (cube) return { cube, column: undefined as AnchorCubeColumn | undefined };
    for (const item of cubes.filter(isPartnerImpactCube)) {
      const column = item.columns.find(
        (entry) => entry.id === id || String(entry.pageId || "") === String(id)
      );
      if (column) return { cube: item, column };
    }
    return { cube: undefined, column: undefined };
  };

  const partnerPages = cubes.filter(isPartnerImpactCube);

  const persist = async (next: {
    localPartner?: ImpactLocalPartner | null;
    updates?: ImpactFieldUpdate[];
  }) => {
    const cardId = Number(mainCardId) || 0;
    if (!cardId) throw new Error("This Card is unavailable.");

    const base = asRecord(cardContent);
    const content: Record<string, unknown> = { ...base };
    if (next.localPartner === null) {
      content.local_partner = null;
    } else if (next.localPartner) {
      content.local_partner = serializeImpactLocalPartner(next.localPartner);
    }
    if (next.updates) {
      content.impact_updates = next.updates.map(serializeImpactFieldUpdate);
    }

    const headers: Record<string, string> = {};
    if (isCreator && talentHeader) {
      headers["talent-id"] = talentHeader;
    }

    await axiosInstance.patch(`/main-card/${cardId}`, { content }, { headers });
    invalidateDeckMainCardByIdQueries(queryClient, cardId);
    void queryClient.invalidateQueries({ queryKey: ["deck-card-thumbnails"] });
  };

  const assignPartner = async (user: {
    id: number;
    username?: string;
    first_name?: string;
    last_name?: string | null;
    profile_image?: string | null;
  }) => {
    const cardId = Number(mainCardId) || 0;
    if (!cardId) {
      enqueueSnackbar("This Card is unavailable.", { variant: "error" });
      return;
    }
    if (!talentHeader) {
      enqueueSnackbar("Talent session is required to invite a partner.", {
        variant: "error",
      });
      return;
    }
    setSaving(true);
    try {
      await inviteImpactPartner({
        mainCardId: cardId,
        userId: user.id,
        talentId: talentHeader,
      });
      setInvitedPartner({
        userId: user.id,
        username: user.username,
        name:
          [user.first_name, user.last_name].filter(Boolean).join(" ") || user.username,
        profileImage: user.profile_image ?? undefined,
        assigned: true,
        assignedAt: new Date().toISOString(),
      });
      invalidateDeckMainCardByIdQueries(queryClient, cardId);
      void queryClient.invalidateQueries({ queryKey: ["deck-card-thumbnails"] });
      void queryClient.invalidateQueries({ queryKey: ["card-thumbnail-by-id"] });
      void queryClient.invalidateQueries({ queryKey: ["main-card-by-id"] });
      void queryClient.invalidateQueries({ queryKey: ["charm-partner-card-thumbnails"] });
      void queryClient.invalidateQueries({ queryKey: ["charm-talent-created-card-thumbnails"] });
      enqueueSnackbar("Local partner invited.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        readApiErrorMessage(error, "Could not invite this partner."),
        { variant: "error" }
      );
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const unassignPartner = async () => {
    if (!isCreator) return;
    setSaving(true);
    try {
      await persist({ localPartner: null });
      enqueueSnackbar("Local partner removed.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Could not remove the partner.", { variant: "error" });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const saveUpdate = async (draft: ImpactFieldUpdate) => {
    if (!isCreator && !isPartner) return;

    const existingColumn = findUpdateColumn(draft.id);
    const images = draft.imageUrls?.filter(Boolean) ?? (draft.imageUrl ? [draft.imageUrl] : []);
    const experienceType = draft.videoUrl ? "Video" : "Image";
    const experience = {
      title: draft.title,
      description: draft.description ?? "",
      url: experienceType === "Video" ? draft.videoUrl ?? "" : images[0] ?? "",
      urls: experienceType === "Image" ? images : [],
      address: draft.locationName || draft.mapsUrl || "",
    };

    setSaving(true);
    try {
      if (existingColumn) {
        await editColumn(UPDATES_CUBE_ID, existingColumn.id, {
          title: draft.title,
          description: draft.description,
          experienceType,
          experience,
        });
        enqueueSnackbar("Anchor cube saved.", { variant: "success" });
        return;
      }

      const existingCube = findPartnerCube(draft.id);
      if (existingCube) {
        await editCube(existingCube.id, {
          title: draft.title,
          description: draft.description,
          experienceType,
          experience,
        });
        enqueueSnackbar("Anchor cube saved.", { variant: "success" });
        return;
      }

      if (draft.attachToCubeId) {
        const parent = findPartnerCube(draft.attachToCubeId);
        if (!parent) {
          throw new Error("Choose a page to add below.");
        }
        await addColumn(parent.id, {
          title: draft.title,
          description: draft.description,
          experienceType,
          experience,
        });
        enqueueSnackbar("Subpage added.", { variant: "success" });
        return;
      }

      await addCube({
        title: draft.title,
        description: draft.description,
        experienceType,
        experience,
        creator: "PARTNER",
      });
      enqueueSnackbar("Page added.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Could not add this Anchor cube.", { variant: "error" });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const approveUpdate = async (id: string, status: ImpactPageVerifyStatus = "Verified") => {
    if (!isCreator) return;
    const column = findUpdateColumn(id);
    const partnerContent = findPartnerContent(id);
    const pageId = Number(column?.pageId ?? partnerContent.column?.pageId ?? partnerContent.cube?.pageId ?? id) || 0;
    if (!pageId) {
      enqueueSnackbar("This content is not ready to verify yet.", { variant: "warning" });
      return;
    }
    if (!talentHeader) {
      enqueueSnackbar("Talent session is required to verify this content.", {
        variant: "error",
      });
      return;
    }

    setSaving(true);
    try {
      await verifyImpactPage({ pageId, talentId: talentHeader, status });
      const cardId = Number(mainCardId) || 0;
      if (cardId) {
        invalidateDeckMainCardByIdQueries(queryClient, cardId);
        void queryClient.invalidateQueries({ queryKey: ["main-card-by-id", cardId] });
        void queryClient.invalidateQueries({ queryKey: ["main-card-by-id"] });
      }
      enqueueSnackbar(
        status === "Rejected" ? "Content rejected." : "Content verified.",
        { variant: status === "Rejected" ? "info" : "success" }
      );
    } catch (error) {
      enqueueSnackbar(readApiErrorMessage(error, "Could not update this content."), {
        variant: "error",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteUpdate = async (id: string) => {
    const column = findUpdateColumn(id);
    if (column) {
      setSaving(true);
      try {
        await removeColumn(UPDATES_CUBE_ID, column.id);
        enqueueSnackbar("Anchor cube removed.", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Could not remove this Anchor cube.", { variant: "error" });
        throw error;
      } finally {
        setSaving(false);
      }
      return;
    }

    const partnerContent = findPartnerContent(id);
    if (partnerContent.column && partnerContent.cube) {
      setSaving(true);
      try {
        await removeColumn(partnerContent.cube.id, partnerContent.column.id);
        enqueueSnackbar("Subpage removed.", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Could not remove this subpage.", { variant: "error" });
        throw error;
      } finally {
        setSaving(false);
      }
      return;
    }

    if (partnerContent.cube) {
      setSaving(true);
      try {
        await removeCube(partnerContent.cube.id);
        enqueueSnackbar("Page removed.", { variant: "success" });
      } catch (error) {
        enqueueSnackbar("Could not remove this page.", { variant: "error" });
        throw error;
      } finally {
        setSaving(false);
      }
      return;
    }

    const target = data.impactUpdates.find((item) => item.id === id);
    if (!target) return;
    if (!isCreator && (target.status === "approved" || target.createdByUserId !== viewerId)) {
      enqueueSnackbar("Approved items can only be removed by the card creator.", {
        variant: "warning",
      });
      return;
    }
    setSaving(true);
    try {
      await persist({
        updates: data.impactUpdates.filter((item) => item.id !== id),
      });
      enqueueSnackbar("Update removed.", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Could not remove this update.", { variant: "error" });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const visibleUpdates = useMemo(() => {
    const fromPartnerPages = cubes
      .filter(
        (cube) =>
          isPartnerImpactCube(cube) &&
          columnExperienceHasContent(cube.experienceType, cube.experience)
      )
      .map(reportFromCube);
    const fromColumns = (updatesCube?.columns ?? [])
      .filter((column) => columnExperienceHasContent(column.experienceType, column.experience))
      .map(reportFromColumn);
    const fromPages = [...fromPartnerPages, ...fromColumns];
    const leftover = data.impactUpdates.filter(
      (item) => !fromPages.some((pageItem) => pageItem.id === item.id)
    );
    const all = [...fromPages, ...leftover];
    if (isCreator || isPartner) return all;
    return all.filter((item) => item.status === "approved");
  }, [cubes, data.impactUpdates, isCreator, isPartner, updatesCube?.columns]);

  return {
    viewerId,
    isCreator,
    partner,
    assigned,
    isPartner,
    saving,
    visibleUpdates,
    partnerPages,
    assignPartner,
    unassignPartner,
    saveUpdate,
    approveUpdate,
    deleteUpdate,
  };
}
